export type Progress = {
  /** Total content length */
  total: number
  /** Downloaded content length */
  transferred: number
  /** Download progress as percentage in [0,1] range */
  complete: number
}

/** Creates a new `Response` instance from the given response, with added ability to track the response‘s body download progress. */
export const createResponseWithProgress = (
  response: Response,
  onProgress: (progress: Progress) => void,
) => {
  if (!response.body) return response

  let transferred = 0
  const total = getContentLength(response) ?? 0

  return new Response(
    response.body.pipeThrough(
      new TransformStream({
        transform(chunk, ctrl) {
          ctrl.enqueue(chunk)

          transferred += chunk.byteLength

          if (transferred < total) onProgress({ total, transferred, complete: transferred / total })
        },
        flush() {
          onProgress({ total, transferred, complete: 1 })
        },
      }),
    ),
    response,
  )
}

const getContentLength = ({ headers }: Response) => {
  let length: number | undefined
  if (headers.has("content-length")) length = Number(headers.get("content-length"))

  // Take X-Goog-Content-Length header from Google Cloud Storage into consideration
  for (const [key, value] of headers.entries())
    if (/^x-.*\bcontent-length$/i.test(key)) {
      const xLength = Number(value)
      return length ? Math.max(length, xLength) : xLength
    }

  return length
}
